import { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { CompletedWalk } from "../types";

interface UserData {
  userName: string;
  totalWalks: number;
  totalMiles: number;
  streak: number;
  lastWalkDate: string;
  completedWalks: CompletedWalk[];
}

const DEFAULT_DATA: UserData = {
  userName: "",
  totalWalks: 0,
  totalMiles: 0,
  streak: 0,
  lastWalkDate: "",
  completedWalks: [],
};

export function useFirestore(userId: string | null) {
  const [userData, setUserData] = useState<UserData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const ref = doc(db, "users", userId);
    getDoc(ref).then(snap => {
      if (snap.exists()) {
        setUserData(snap.data() as UserData);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  const saveWalk = async (
    userId: string,
    walk: CompletedWalk,
    currentData: UserData,
  ) => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    const newStreak = currentData.lastWalkDate === yesterday
      ? currentData.streak + 1
      : currentData.lastWalkDate === today
      ? currentData.streak
      : 1;

    const updated: UserData = {
      ...currentData,
      totalWalks: currentData.totalWalks + 1,
      totalMiles: parseFloat((currentData.totalMiles + parseFloat(walk.distance)).toFixed(1)),
      streak: newStreak,
      lastWalkDate: today,
      completedWalks: [walk, ...currentData.completedWalks].slice(0, 50),
    };

    await setDoc(doc(db, "users", userId), updated);
    setUserData(updated);
    return updated;
  };

  const saveName = async (userId: string, name: string) => {
    const updated = { ...userData, userName: name };
    await setDoc(doc(db, "users", userId), updated);
    setUserData(updated);
  };

  return { userData, loading, saveWalk, saveName };
}
